import { useImperativeHandle, useMemo, type Ref } from "react";
import type { Task } from "@common/types/task";
import { TasksBulkActionsFooter } from "../TasksBulkActionsFooter";
import { TasksBulkDeleteModal } from "../TasksBulkDeleteModal";
import { useTaskBulkActions } from "../useTaskBulkActions";

/** What S and A on a selected card open: the island's Set status and Assign member. */
export interface TaskBoardIslandHandle {
    setStatus: () => void;
    assignMember: () => void;
}

interface TaskBoardBulkIslandProps {
    /** Set by the board, which also routes S and A here only while it's shown. */
    isShown: boolean;
    selectedTasks: Task[];
    onClearSelection: () => void;
    ref?: Ref<TaskBoardIslandHandle>;
}

/**
 * The workspace board's command island: List's bulk actions footer and
 * palette for two or more selected cards. One card's actions live in its
 * kebab and right-click menu instead.
 */
export const TaskBoardBulkIsland = ({
    isShown,
    selectedTasks,
    onClearSelection,
    ref,
}: Readonly<TaskBoardBulkIslandProps>) => {
    const selectedTaskIds = useMemo(
        () => selectedTasks.map((task) => task.id),
        [selectedTasks],
    );
    const {
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleSetStatusClick,
        handleAssignMemberClick,
        handleBulkDeleteConfirm,
        handleOpenBulkActions,
    } = useTaskBulkActions({
        selectedTaskIds,
        selectedTasks,
        clearSelection: onClearSelection,
    });
    useImperativeHandle(
        ref,
        () => ({
            setStatus: handleSetStatusClick,
            assignMember: handleAssignMemberClick,
        }),
        [handleSetStatusClick, handleAssignMemberClick],
    );

    return (
        <>
            <TasksBulkActionsFooter
                show={isShown}
                onDeselectRows={onClearSelection}
                onSetStatus={handleSetStatusClick}
                onAssignMember={handleAssignMemberClick}
                onOpenBulkActions={handleOpenBulkActions}
                selectedCount={selectedTasks.length}
                selectedTasks={selectedTasks}
            />
            <TasksBulkDeleteModal
                show={showDeleteConfirm}
                selectedCount={selectedTasks.length}
                onConfirm={handleBulkDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
            />
        </>
    );
};
